// External dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

// Internal dependencies
import { searchUsers } from '@/modules/users/client/api/search-users.api.js';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';
import UsersResults from './UsersResults';

const MINIMUM_QUERY_LENGTH = 3;

export default function SearchUsers() {
  const { t } = useTranslation('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchFailed, setSearchFailed] = useState(false);

  async function fetchUsers(query: string) {
    setIsSearching(true);
    setHasSearched(true);
    setUsers([]);
    setSearchFailed(false);
    try {
      const { data: users } = await searchUsers(query);
      setUsers(users || []);
      setIsSearching(false);
    } catch {
      setSearchFailed(true);
    } finally {
      setIsSearching(false);
    }
  }

  useEffect(() => {
    const urlSearchQuery = new URL(window.location.href).searchParams.get(
      'search',
    );

    if (urlSearchQuery) {
      setHasSearched(true);
      setSearchQuery(urlSearchQuery);
      if (urlSearchQuery.length >= MINIMUM_QUERY_LENGTH) {
        fetchUsers(urlSearchQuery);
      }
    }
  }, []);

  const searchForm = (
    <form
      className="form-group search-form-group"
      id="search-users-form"
      onSubmit={event => {
        event.preventDefault();
        fetchUsers(searchQuery);
      }}
    >
      <div className="input-group">
        <input
          aria-label={t('Search members')}
          disabled={isSearching}
          className="form-control input-lg"
          onChange={({ target: { value } }) => {
            setHasSearched(false);
            setSearchFailed(false);
            setSearchQuery(value);
            setUsers([]);
          }}
          placeholder={t('Name, username, location or tagline…')}
          maxLength={120}
          tabIndex={0}
          type="text"
          value={searchQuery}
        />
        <span className="input-group-btn">
          <span>
            <button
              aria-label={t('Clear members search')}
              className="btn btn-lg btn-default"
              disabled={
                isSearching || searchQuery.length < MINIMUM_QUERY_LENGTH
              }
              onClick={() => {
                setSearchQuery('');
                setHasSearched(false);
                setSearchFailed(false);
                setUsers([]);
              }}
              type="button"
            >
              <i className="icon-close"></i>
            </button>
          </span>
          <span>
            <button
              aria-label={t('Search members')}
              className="btn btn-lg btn-default"
              disabled={
                isSearching || searchQuery.length < MINIMUM_QUERY_LENGTH
              }
              type="submit"
            >
              <i className="icon-search"></i>
              <span className="hidden-xs">{String(t('Search'))}</span>
            </button>
          </span>
        </span>
      </div>
    </form>
  );

  return (
    <section className="container container-spacer">
      {searchForm}
      {isSearching && <LoadingIndicator />}
      {searchFailed && (
        <p role="alert">
          {String(
            t(
              'Could not search members. Please try again with a more specific search.',
            ),
          )}
        </p>
      )}
      {!isSearching && !searchFailed && hasSearched && (
        <UsersResults users={users} query={searchQuery} />
      )}
    </section>
  );
}
